import React, { FC } from 'react'
import _ from 'lodash'

import { HoverPreview_s, HoverTitle_s } from 'components/table/hovers/hover-styles'
import BasicHover from 'components/table/hovers/basic'
import DetailedHover from 'components/table/hovers/detailed-hover'
import RentBasicHover from './rent-basic'
import LineItemDetailedHover from 'components/table/hovers/line-item-detailed'
import RecoveryPoolsHover from 'components/table/hovers/recovery-pools-hover'
import ProRataRecoveryHover from 'components/table/hovers/pro-rata-recovery'
import RecoveriesHover from 'components/table/hovers/recoveries'

type HoverProps = {
  config?: {},
  data: [],
  field: string,
  namedItems?: object,
  title: string,
  description?: string
}

const HoverPreview: FC<HoverProps> = (props) => {
  const basicStates = {
    advanced: 'Advanced',
    amount: 'Amount',
    amt: 'Amount',
    basic: 'Basic',
    amtArea: 'Amt/Area',
    namedItem: 'Named Item',
    percentEGR: '% EGR',
    spreadEvenly: 'Spread Evenly',
    prop13Tax: 'Prop 13 Tax',
    proRataRecovery: 'Pro Rata Recovery',
    tenantMgmtFee: 'Tenant Mgmt Fee',
  }
  const detailedStates = {
    advanced: 'Advanced',
    byLeaseType: 'By Lease Type',
    byTenantGroup: 'By Tenant Group',
    detailed: 'Detailed',
    detailedMo: 'Detailed Monthly',
    detailedPerArea: 'Detailed Per Area',
    detailedPercentLineItem: 'Detailed % of Line Item',
    detailedYr: 'Detailed Annual',
    lineItemDetailed: 'Detailed',
    percent: 'Percent',
    percentLineItem: '% of Line',
    percentTenantRevenue: '% Tenant Revenue',
    withinLease: 'Within Lease',
    recoverypooldetail: "Recovery Pool Detail"
  }
  const stateNames = {
    ...basicStates,
    ...detailedStates
  }

  let state                 = _.get(props, `data.${props.field}.state`) || _.get(props, 'data.inputtype');
  if (state === 'spreadEvenly') state = 'basic'
  if (props.field === "recoverypooldetail") state = 'recoverypooldetail'
  if (props.field === 'tenantgroupdetail') state = 'detailed'
  if (props.field === 'users') state = 'detailed'
  if (props.field === 'defaultportfolios') state = 'detailed'
  if (props.field === 'approles') state = 'detailed'
  if (props.field === 'useraccess') state = 'detailed'
  let   stateName           = stateNames[state];
  const isBasic             = basicStates[state] !== undefined
  const isDetailed          = detailedStates[state] !== undefined || props.field === 'tenantgroupdetail' || props.field === "recoverypooldetail" || props.field === 'users' || props.field === 'defaultportfolios' || props.field === 'useraccess'
  let hasHover              = props.config && (props.config[state] || (state === 'detailed' && props.config['lineItemDetailed']));
  if (props.field === 'tenantgroupdetail' || props.field === "recoverypooldetail" || props.field === 'recoverydetail') hasHover = true

  const isRecoveryPools = (props.field === "recoverypooldetail") ? true : false
  const isProRataRecovery = (props.field === 'recoverydetail' && props.config && props.config['proRataRecovery'] && state === 'proRataRecovery')
  const isRecoveries = (props.field === 'recoverydetail' && props.config?.['recoverydetail'])
  const isLineItemdDetailed = state === 'detailedMo' || (state === 'detailed' && props.config && props.config['lineItemDetailed']) || props.config && props.config['detailedPerArea'] && (state === 'detailedPerArea' || state === 'detailedPercentLineItem') || false
  const isRentDetailed      = props.config?.['basic']?.[0][0]?.['key'][0] === 'rentdetail' ?? false
  const code = _.get(props, "data.code") || _.get(props, "data.unitdescription") || _.get(props, "data.description") || _.get(props, "data.tenant") || ""
  let description = _.get(props, "description")
  if(!description) description = _.get(code, "length") > 20 ? code.slice(0, 20) + "..." : code

  if(_.get(props, "config.injected")) {
    let title = stateName ? `${description} | ${props.title} : ${stateName}` : `${description} | ${props.title}`
    return (
      <HoverPreview_s>
        <HoverTitle_s>{title}</HoverTitle_s>
        {_.get(props, "config.injected")(props)}
      </HoverPreview_s>
    )
  } else if (props.data[props.field] || (props.config && isBasic && props.config['basic'][0]['ignoreField'] && props.data['inputtype'])){
    return (
      <HoverPreview_s>
        <HoverTitle_s>{stateName ? `${description} | ${props.title} : ${stateName}` : `${description} | ${props.title}`}</HoverTitle_s>

        {isBasic && hasHover && props.config && isRentDetailed && <RentBasicHover data={props.data} config={props.config} inflationData={props.namedItems && props.namedItems['inflation']} />}

        {isBasic && hasHover && isProRataRecovery && <ProRataRecoveryHover data={props.data} config={props.config!} areaMeasuresData={props.namedItems?.['areaMeasures']} recoveryPoolsData={props.namedItems?.['recoveryPools']} />}

        {isDetailed && hasHover && isLineItemdDetailed && <LineItemDetailedHover data={props.data} config={props.config!} areaMeasureData={props.namedItems && props.namedItems['areaMeasures']} inflationData={props.namedItems && props.namedItems['inflation']} lineItemData={props.namedItems && props.namedItems['lineItems']} state={state} />}

        {isDetailed && hasHover && isRecoveryPools && <RecoveryPoolsHover data={props.data} config={props.config!} lineItemData={props.namedItems && props.namedItems['lineItems']} tenantContributionsData={props.namedItems && props.namedItems['tenantContributions']} state={state} />}

        {isRecoveries && hasHover && <RecoveriesHover data={props.data} config={props.config!} />}

        {isBasic && hasHover && !isRentDetailed && !isProRataRecovery &&
          <BasicHover
            data={props.data}
            state={state}
            config={props.config!}
            downtimeData={props.namedItems && props.namedItems['downtime']}
            freeRentData={props.namedItems && props.namedItems['freeRent']}
            increaseData={props.namedItems && props.namedItems['increase']}
            inflationData={props.namedItems && props.namedItems['inflation']}
            areaMeasureData={props.namedItems && props.namedItems['areaMeasures']}
            lineItemData={props.namedItems && props.namedItems['lineItems']}
            valuationData={_.get(props, 'namedItems.valuationItems')}
            payoutData={props.namedItems && props.namedItems['payout']}
            rentData={props.namedItems && props.namedItems['rent']}
            tenantCapitalData={props.namedItems && props.namedItems['tenantCapital']}
          />
        }

        {isDetailed && hasHover && !isLineItemdDetailed &&
          <DetailedHover
            data={props.data}
            config={props.config!}
            field={props.field}
            inflationData={props.namedItems && props.namedItems['inflation']}
            stateName={state}
            tenantGroupsData={props.namedItems && props.namedItems['tenantGroups']}
            areaMeasuresData={props.namedItems && props.namedItems['areaMeasures'] && props.namedItems['items']}
            namedRecoveriesData={props.namedItems && props.namedItems['namedRecoveries']}
            lineItemData={props.namedItems && props.namedItems['lineItems']}
            systemLineItemData={props.namedItems && props.namedItems['systemLineItems']}
            payoutData={props.namedItems && props.namedItems['payout']}
            rentRollData={props.namedItems && props.namedItems['rentroll']}
            reportTagsData={props.namedItems && props.namedItems['reportTags']}
            usersData={props.namedItems && props.namedItems['users']}
            portfolioData={props.namedItems && props.namedItems && props.namedItems['portfolios']}
            items={
              props?.data[props?.field]?.items
                ? props?.data[props?.field]?.items
                : []
            } />}
      </HoverPreview_s>
    )
  } else return null
}

export default HoverPreview